import React, { useState } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText, // For Delete confirmation
  DialogTitle,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  SelectChangeEvent,
  Chip,
  Grid,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel // For grouping permissions
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import { format } from 'date-fns';

// --- Interfaces --- 
interface ModulePermissions {
  view?: boolean;
  add?: boolean;
  edit?: boolean;
  delete?: boolean;
}

interface ReportPermissions {
  view?: boolean;
  export?: boolean;
}

interface Permissions {
  modules: {
    parameters?: ModulePermissions; // Example
    transactions?: ModulePermissions; // Example
    // Add other modules based on your app structure
  };
  reports: {
    debtRepayment?: ReportPermissions; // Example
    auditTrail?: ReportPermissions; // Example
    // Add other report categories
  };
  admin: {
    manageUsers?: boolean;
    manageRoles?: boolean;
    systemConfig?: boolean;
  };
}

interface Role {
  id: string;
  roleName: string;
  description: string;
  permissions: Permissions;
  status: 'Active' | 'Inactive';
  creationDate: Date;
  lastUpdatedDate: Date;
  createdBy: string;
  lastUpdatedBy: string;
}

// --- Mock Data & Definitions ---
const initialRoles: Role[] = [
  {
    id: 'ROLE001',
    roleName: 'Back-end Administrator',
    description: 'Full system access.',
    permissions: { // Grant all for admin
      modules: {
        parameters: { view: true, add: true, edit: true, delete: true },
        transactions: { view: true, add: true, edit: true, delete: true },
      },
      reports: {
        debtRepayment: { view: true, export: true },
        auditTrail: { view: true, export: true },
      },
      admin: { manageUsers: true, manageRoles: true, systemConfig: true },
    },
    status: 'Active',
    creationDate: new Date(2023, 0, 1),
    lastUpdatedDate: new Date(2023, 0, 1),
    createdBy: 'System',
    lastUpdatedBy: 'System',
  },
  {
    id: 'ROLE002',
    roleName: 'Employee',
    description: 'Basic transaction and report viewing.',
    permissions: {
      modules: {
        parameters: { view: true },
        transactions: { view: true, add: true },
      },
      reports: {
        debtRepayment: { view: true },
      },
      admin: {},
    },
    status: 'Active',
    creationDate: new Date(2023, 1, 1),
    lastUpdatedDate: new Date(2023, 5, 1),
    createdBy: 'System',
    lastUpdatedBy: 'admin.user',
  },
];

const statuses = ['Active', 'Inactive'];

// Define available permissions structure for the form
const availablePermissions = {
  modules: {
    title: 'Modules',
    items: {
      parameters: { label: 'Parameters', perms: ['view', 'add', 'edit', 'delete'] },
      transactions: { label: 'Transactions', perms: ['view', 'add', 'edit', 'delete'] },
      // Add more modules
    }
  },
  reports: {
    title: 'Reports',
    items: {
      debtRepayment: { label: 'Debt Repayment', perms: ['view', 'export'] },
      auditTrail: { label: 'Audit Trail', perms: ['view', 'export'] },
       // Add more reports
    }
  },
  admin: {
    title: 'Administration',
    items: {
      manageUsers: { label: 'Manage Users' },
      manageRoles: { label: 'Manage Roles' },
      systemConfig: { label: 'System Configuration' },
    }
  }
};

// Helper to get nested permission value
const getPermissionValue = (permissions: Permissions | undefined | null, path: string[]): boolean => {
  if (!permissions) return false;
  let current: any = permissions;
  for (const key of path) {
    if (current === undefined || current === null) return false;
    current = current[key];
  }
  return !!current; // Return true if value exists and is truthy
};

// Helper to set nested permission value
const setPermissionValue = (permissions: Permissions, path: string[], value: boolean): Permissions => {
  const newPermissions = JSON.parse(JSON.stringify(permissions)); // Deep clone
  let current: any = newPermissions;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    if (current[key] === undefined || current[key] === null) {
      current[key] = {};
    }
    current = current[key];
  }
  current[path[path.length - 1]] = value;
  return newPermissions;
};

// --- Component ---
const RoleManagement: React.FC = () => {
  const [rolesData, setRolesData] = useState<Role[]>(initialRoles);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit' | 'view'>('add');
  const [currentRole, setCurrentRole] = useState<Partial<Role> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

  const [searchTerm, setSearchTerm] = useState('');

  // Derived state for filtered roles
  const filteredRoles = rolesData.filter(role =>
    role.roleName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenDialog = (mode: 'add' | 'edit' | 'view', role?: Role) => {
    setDialogMode(mode);
    if (mode === 'add') {
      // Initialize with empty permissions structure
      const initialPerms: Permissions = {
        modules: {},
        reports: {},
        admin: {}
      };
      setCurrentRole({ permissions: initialPerms });
    } else if (role) {
      setCurrentRole({ ...role });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentRole(null);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setCurrentRole(prev => (prev ? { ...prev, [name]: value } : null));
  };

  const handlePermissionChange = (path: string[], checked: boolean) => {
    setCurrentRole(prev => {
      if (!prev?.permissions) return prev;
      const newPermissions = setPermissionValue(prev.permissions, path, checked);
      return { ...prev, permissions: newPermissions };
    });
  };

  const handleSave = () => {
    if (!currentRole || !currentRole.roleName) {
        alert("Role Name is required."); // Basic validation
        return;
    }

    const now = new Date();
    const loggedInUser = 'current.user'; // Simulate

    if (dialogMode === 'add') {
      const newRole: Role = {
        id: `ROLE${String(rolesData.length + 1).padStart(3, '0')}`,
        roleName: currentRole.roleName,
        description: currentRole.description || '',
        permissions: currentRole.permissions || { modules: {}, reports: {}, admin: {} },
        status: currentRole.status || 'Active',
        creationDate: now,
        lastUpdatedDate: now,
        createdBy: loggedInUser,
        lastUpdatedBy: loggedInUser,
      };
      setRolesData([...rolesData, newRole]);
    } else if (dialogMode === 'edit' && currentRole.id) {
      setRolesData(rolesData.map(role =>
        role.id === currentRole.id
          ? { ...role, ...currentRole, lastUpdatedDate: now, lastUpdatedBy: loggedInUser } as Role
          : role
      ));
    }
    handleCloseDialog();
  };

  const openDeleteConfirm = (role: Role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  }

  const closeDeleteConfirm = () => {
    setRoleToDelete(null);
    setDeleteDialogOpen(false);
  }

  const handleDelete = () => {
    if (!roleToDelete) return;
    // Add validation check here (e.g., check if role is assigned to any user)
    // For now, just delete directly
    console.log(`Deleting role ${roleToDelete.id}`);
    setRolesData(rolesData.filter(role => role.id !== roleToDelete.id));
    closeDeleteConfirm();
  };

  // --- Render Permission Checkboxes --- 
  const renderPermissionCheckboxes = (categoryKey: keyof Permissions, categoryConfig: any) => (
    <Box key={categoryKey} sx={{ mb: 2 }}>
      <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>{categoryConfig.title}</FormLabel>
      <Grid container spacing={1}>
        {Object.entries(categoryConfig.items).map(([itemKey, itemConfig]: [string, any]) => (
          <Grid item xs={12} sm={6} key={itemKey}>
            <FormControlLabel
                control={<Typography sx={{ fontWeight: 500 }}>{itemConfig.label}</Typography>}
                label=""
                sx={{ mb: -1 }} // Adjust spacing
              />
            <FormGroup sx={{ pl: 2 }}>
              {itemConfig.perms ? (
                // Module/Report style permissions (view, add, etc.)
                itemConfig.perms.map((permKey: string) => (
                  <FormControlLabel
                    key={permKey}
                    control={
                      <Checkbox
                        size="small"
                        checked={getPermissionValue(currentRole?.permissions, [categoryKey, itemKey, permKey])}
                        onChange={(e) => handlePermissionChange([categoryKey, itemKey, permKey], e.target.checked)}
                        disabled={dialogMode === 'view'}
                      />
                    }
                    label={permKey.charAt(0).toUpperCase() + permKey.slice(1)}
                  />
                ))
              ) : (
                 // Admin style permissions (simple true/false)
                 <FormControlLabel
                    control={
                      <Checkbox
                         size="small"
                         checked={getPermissionValue(currentRole?.permissions, [categoryKey, itemKey])}
                         onChange={(e) => handlePermissionChange([categoryKey, itemKey], e.target.checked)}
                         disabled={dialogMode === 'view'}
                       />
                    }
                    label="Allow"
                 />
              )}
            </FormGroup>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/dashboard">
          Home
        </MuiLink>
        <Typography color="text.primary">Administration</Typography>
        <Typography color="text.primary">Role Management</Typography>
      </Breadcrumbs>

      {/* Header and Filters */}
      <Paper sx={{ p: 2, mb: 3, border: '1px solid #E5E7EB', borderRadius: 2, bgcolor: '#FFFFFF' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8} md={9}>
            <TextField
              fullWidth
              placeholder="Search by Role Name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#6B7280' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('add')}
              sx={{ bgcolor: '#6366F1', '&:hover': { bgcolor: '#4F46E5' }, textTransform: 'none', px: 3, width: { xs: '100%', sm: 'auto' } }}
            >
              Add New Role
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Role Table */}
      <TableContainer component={Paper} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: '#F9FAFB', fontWeight: 'bold' } }}>
              <TableCell>Role ID</TableCell>
              <TableCell>Role Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRoles.map((role) => (
              <TableRow key={role.id} hover>
                <TableCell>{role.id}</TableCell>
                <TableCell>{role.roleName}</TableCell>
                <TableCell>
                  <Chip
                    label={role.status}
                    color={role.status === 'Active' ? 'success' : 'error'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleOpenDialog('view', role)} title="View">
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleOpenDialog('edit', role)} title="Edit">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => openDeleteConfirm(role)} title="Delete">
                    <DeleteIcon fontSize="small" color="error"/>
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit/View Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{dialogMode === 'add' ? 'Add New Role' : dialogMode === 'edit' ? 'Edit Role' : 'View Role Details'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {dialogMode !== 'add' && (
              <Grid item xs={12} sm={6}>
                <TextField label="Role ID" value={currentRole?.id || ''} fullWidth disabled InputLabelProps={{ shrink: true }} />
              </Grid>
            )}
            <Grid item xs={12} sm={dialogMode === 'add' ? 12 : 6}>
              <TextField
                label="Role Name"
                name="roleName"
                value={currentRole?.roleName || ''}
                onChange={handleInputChange}
                fullWidth
                required
                disabled={dialogMode === 'view'}
                inputProps={{ maxLength: 50 }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={currentRole?.description || ''}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
                disabled={dialogMode === 'view'}
                inputProps={{ maxLength: 200 }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
               <FormControl fullWidth required={dialogMode !== 'view'}>
                 <InputLabel>Status</InputLabel>
                 <Select
                   label="Status"
                   name="status"
                   value={currentRole?.status || ''}
                   onChange={handleInputChange}
                   disabled={dialogMode === 'view'}
                 >
                   {statuses.map((status) => (
                     <MenuItem key={status} value={status}>{status}</MenuItem>
                   ))}
                 </Select>
               </FormControl>
            </Grid>

             {/* Permissions Section */}
             <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Permissions</Typography>
                {renderPermissionCheckboxes('modules', availablePermissions.modules)}
                {renderPermissionCheckboxes('reports', availablePermissions.reports)}
                {renderPermissionCheckboxes('admin', availablePermissions.admin)}
             </Grid>

            {dialogMode !== 'add' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField label="Creation Date" value={currentRole?.creationDate ? format(currentRole.creationDate, 'P p') : ''} fullWidth disabled InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Created By" value={currentRole?.createdBy || ''} fullWidth disabled InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Last Updated Date" value={currentRole?.lastUpdatedDate ? format(currentRole.lastUpdatedDate, 'P p') : ''} fullWidth disabled InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Last Updated By" value={currentRole?.lastUpdatedBy || ''} fullWidth disabled InputLabelProps={{ shrink: true }} />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          <Button onClick={handleCloseDialog} sx={{ color: '#6B7280' }}>Cancel</Button>
          {dialogMode !== 'view' && (
            <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#6366F1', '&:hover': { bgcolor: '#4F46E5' } }}>Save</Button>
          )}
        </DialogActions>
      </Dialog>

       {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteConfirm}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the role "{roleToDelete?.roleName}"? This action cannot be undone.
            {/* Add warning here if role is assigned */} 
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm}>Cancel</Button>
          <Button onClick={handleDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagement; 